/**
 * État de sélection de l'import Pocket (fonctions pures, testées séparément du rendu).
 */

/** Décision par défaut : un élément nouveau est coché, un élément déjà importé ne l'est jamais. */
export const initialChoices = (preview) => {
  const choices = {};
  (preview?.items || []).forEach((item) => {
    const isNew = item.dedup?.status === 'new';
    choices[item.id] = {
      selected: isNew,
      decision: isNew ? 'import' : 'newRubric',
      variant: item.target?.variant || null
    };
  });
  return choices;
};

export const canAttachRevision = (item) => {
  const d = item.dedup;
  return Boolean(d && (d.status === 'newerRevision' || d.status === 'olderRevision') && d.previous && d.previous.rubricStillPresent);
};

/** Références à rattacher : seulement celles des éléments cochés qui créent une rubrique. */
export const requiredReferenceIds = (preview, choices) => {
  const ids = new Set();
  (preview?.items || []).forEach((item) => {
    const choice = choices[item.id];
    if (!choice || !choice.selected || choice.decision === 'attachRevision') return;
    (item.personRefs || []).concat(item.intervenantRefs || []).forEach((id) => ids.add(id));
  });
  return (preview?.references || []).filter((r) => ids.has(r.id)).map((r) => r.id);
};

/** Rattachements initiaux : uniquement ceux mémorisés pour ce dossier et toujours valides (jamais l'importateur par défaut). */
export const initialMappings = (preview) => {
  const mappings = {};
  (preview?.references || []).forEach((ref) => {
    if (ref.previousMapping && ref.previousMapping.valid) mappings[ref.id] = ref.previousMapping.entityId;
  });
  return mappings;
};

export const buildImportPayload = ({ preview, choices, mappings, importId, fileContent, secret }) => {
  const selection = (preview?.items || [])
    .filter((item) => choices[item.id] && choices[item.id].selected)
    .map((item) => {
      const choice = choices[item.id];
      const decision = item.dedup?.status === 'new' ? 'import' : choice.decision;
      const entry = { itemId: item.id, decision };
      if (choice.variant && item.target?.variant && choice.variant !== item.target.variant) entry.variant = choice.variant;
      return entry;
    });
  const required = requiredReferenceIds(preview, choices);
  const referenceMappings = required
    .filter((refId) => typeof mappings[refId] === 'string' && mappings[refId].length > 0)
    .map((refId) => ({ refId, entityId: mappings[refId] }));
  return { fileContent, secret, importId, selection, referenceMappings };
};

export const missingReferences = (preview, choices, mappings) =>
  requiredReferenceIds(preview, choices).filter((refId) => !(typeof mappings[refId] === 'string' && mappings[refId].length > 0));

/**
 * Normalisation légère pour activer le bouton (la validation complète est faite par le serveur) :
 * code de 6 chiffres, ou secret de 25 caractères d'un export antérieur.
 */
export const secretLooksComplete = (value) => {
  if (typeof value !== 'string') return false;
  const cleaned = value.toUpperCase().replace(/[\s\-_.\u2010-\u2015]/g, '');
  return /^[0-9]{6}$/.test(cleaned) || cleaned.length === 25;
};
